import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { paletteColorOf } from './LibraryListParts'

/**
 * SlidingSelection — story 167 (PO, 29/09/2026): in a group of buttons the mark of the chosen one does not jump, it
 * slides from the button it leaves to the one it goes to, as the underline of the panel's tabs already does.
 *
 * `useSlidingSelection` measures the pressed button of a group (`aria-pressed`), again whenever the choice changes
 * or the group changes size; `SlidingMark` is drawn under the buttons, as a raised pill or as an underline.
 */
const EASE = '280ms cubic-bezier(.4, 0, .2, 1)'

export const useSlidingSelection = (selectedKey) => {
  const groupRef = useRef(null)
  const [box, setBox] = useState(null)
  useLayoutEffect(() => {
    const group = groupRef.current
    if (!group) {
      return undefined
    }
    const measure = () => {
      const pressed = group.querySelector('[aria-pressed="true"]')
      if (!pressed) {
        setBox(null)
        return
      }
      const outer = group.getBoundingClientRect()
      const inner = pressed.getBoundingClientRect()
      const next = { left: inner.left - outer.left, top: inner.top - outer.top, width: inner.width, height: inner.height }
      setBox((current) => (current && current.left === next.left && current.top === next.top && current.width === next.width && current.height === next.height ? current : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(group)
    return () => observer.disconnect()
  }, [selectedKey])
  return [groupRef, box]
}

export const SlidingMark = ({ box, variant = 'pill', color = 'primary.main' }) => {
  if (!box) {
    return null
  }
  return (
    <Box
      aria-hidden
      data-testid='sliding-mark'
      sx={(theme) => {
        const tone = paletteColorOf(theme, color)
        return {
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',
          transition: 'left ' + EASE + ', width ' + EASE + ', background-color ' + EASE + ', box-shadow ' + EASE,
          ...(variant === 'pill'
            // PO, 29/09/2026: what is chosen is raised, not lit — a lighter surface with a soft dark shadow; the icon
            // on it keeps its own colour.
            ? { borderRadius: '8px', bgcolor: alpha(theme.palette.common.white, 0.08), boxShadow: '0 1px 4px ' + alpha(theme.palette.common.black, 0.45) }
            : { height: 2, borderRadius: '2px', bgcolor: tone }),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }
      }}
      style={variant === 'pill'
        ? { left: box.left, top: box.top, width: box.width, height: box.height }
        : { left: box.left, top: box.top + box.height - 2, width: box.width }}
    />
  )
}
